import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Eye, EyeOff } from "lucide-react";
import { api, errMsg, clipUrl } from "@/lib/api";
import { useMeta } from "@/lib/meta";
import { applyTemplate } from "@/lib/templates";
import { computeLayout } from "@/lib/layout";
import { StampSvg } from "@/components/StampSvg";

const inp = "w-full h-9 px-3 rounded-md border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-yellow/70";

function TemplateCard({ tpl, idx, product, onSaved, onDeleted }) {
  const meta = useMeta();
  const [t, setT] = useState({ ...tpl, text: tpl.lines.join("\n") });
  const set = (k, v) => setT({ ...t, [k]: v });
  const lines = t.text.split("\n").filter((x) => x.trim());
  const preview = product && lines.length ? computeLayout(product, applyTemplate(product, { ...t, lines }, null)) : null;

  const save = async () => {
    const body = { name: t.name, icon: t.icon || null, font: t.font || null, lines, active: t.active, sort: +t.sort || 0 };
    try {
      const r = t.id ? await api.put(`/admin/templates/${t.id}`, body) : await api.post("/admin/templates", body);
      toast.success("Modello salvato");
      onSaved(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Eliminare il modello "${t.name}"?`)) return;
    if (t.id) await api.delete(`/admin/templates/${t.id}`);
    onDeleted();
  };

  return (
    <div className={`rounded-xl border bg-white p-4 grid md:grid-cols-[1fr_260px] gap-4 ${t.active ? "" : "opacity-60"}`} data-testid={`admin-template-${idx}`}>
      <div className="space-y-3">
        <div className="grid grid-cols-[1fr_80px] gap-2">
          <input data-testid={`template-name-${idx}`} className={inp} value={t.name} placeholder="Nome (es. Avvocato)" onChange={(e) => set("name", e.target.value)} />
          <input data-testid={`template-sort-${idx}`} className={inp} type="number" title="Ordine" value={t.sort} onChange={(e) => set("sort", e.target.value)} />
        </div>
        <textarea data-testid={`template-lines-${idx}`} rows={5} className={inp + " h-auto py-2 font-mono text-xs"} value={t.text} placeholder="Una riga del timbro per ogni riga" onChange={(e) => set("text", e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <select data-testid={`template-font-${idx}`} className={inp} value={t.font || ""} onChange={(e) => set("font", e.target.value)}>
            <option value="">Font predefinito del prodotto</option>
            {meta?.fonts.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select data-testid={`template-icon-${idx}`} className={inp} value={t.icon || ""} onChange={(e) => set("icon", e.target.value)}>
            <option value="">Nessuna icona</option>
            {meta?.clipart.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid={`template-toggle-${idx}`} onClick={() => set("active", !t.active)} className="press h-9 px-3 rounded-md border border-slate-200 text-xs font-semibold flex items-center gap-1.5">
            {t.active ? <><Eye className="w-3.5 h-3.5" /> Visibile</> : <><EyeOff className="w-3.5 h-3.5" /> Nascosto</>}
          </button>
          {t.icon && <img src={clipUrl(t.icon)} alt="" className="w-6 h-6" />}
          <span className="flex-1" />
          <button data-testid={`template-delete-${idx}`} onClick={del} className="press h-9 w-9 rounded-md border border-slate-200 text-slate-500 hover:text-red-600 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
          <button data-testid={`template-save-${idx}`} onClick={save} className="press h-9 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold flex items-center gap-1.5 hover:bg-brand-yellowdark"><Save className="w-4 h-4" /> Salva</button>
        </div>
      </div>
      <div className="ink-grid rounded-lg flex items-center justify-center p-3 min-h-[120px]">
        {preview && <StampSvg layout={preview} color="#0A2E5C" scale={Math.min(230 / product.width_mm, 110 / product.height_mm)} />}
      </div>
    </div>
  );
}

export function AdminTemplates() {
  const meta = useMeta();
  const [list, setList] = useState(null);
  const [product, setProduct] = useState(null);
  const load = () => api.get("/admin/templates").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/products/trodat-printy-4913").then((r) => setProduct(r.data)).catch(() => api.get("/products").then((r) => setProduct(r.data[0])));
  }, []);
  const changed = () => { load(); meta?.reload(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-extrabold uppercase">Modelli pronti</h1>
          <p className="text-sm text-slate-500">I modelli visibili compaiono nella scheda "Modelli" dell'editor e si adattano da soli a ogni timbro. Anteprima su {product?.name}.</p>
        </div>
        <button data-testid="admin-new-template-btn" onClick={() => setList([{ name: "Nuovo modello", lines: ["Nome Cognome", "Via Roma 1 - Cesena", "Tel. 0547 000000"], active: true, sort: (list?.length || 0) + 1, icon: null, font: null, _new: Date.now() }, ...(list || [])])}
          className="press shrink-0 h-10 px-4 rounded-md bg-brand-yellow text-brand-navy text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo modello</button>
      </div>
      <div className="space-y-3">
        {list?.map((t, i) => (
          <TemplateCard key={t.id || t._new} tpl={t} idx={i} product={product} onSaved={changed} onDeleted={changed} />
        ))}
      </div>
    </div>
  );
}
